/**
 * 会话详情：聊天原文 + 每条消息背后的中枢决策。
 *
 * **打开这一页会写一条 AuditLog**（`admin.readConversationMessages`）。
 * 查看用户私聊本身就是一次权力行使，即使只读也必须留痕。
 */

import { useState } from "react";
import { Alert, Card, Checkbox, Descriptions, Pagination, Space, Spin, Tag, Typography } from "antd";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { api, type Paged } from "../../services/api";
import { fmtDateTime, fmtNumber, StatusTag, Tags } from "../../components/format";

interface ConversationDetail {
  id: string;
  relationshipId: string;
  userId: string;
  userName: string | null;
  relationshipState: { bond: number; level: number; characterAffinity: number; userPreferredName: string | null } | null;
  product: { key: string; displayName: string; entryKind: string };
  status: string;
  lastSequence: number;
  messageCount: number;
  brainRunCount: number;
  lastMessageAt: string | null;
  createdAt: string | null;
  /** 滚动摘要：会话超出上下文预算时把最早那段折叠掉的产物。 */
  rollingSummary: string | null;
  rollingSummaryUpToSequence: number | null;
  /** 一个会话可以有多段摘要，每次折叠产生一段。 */
  memories: {
    id: string;
    title: string | null;
    summary: string | null;
    tags: string[];
    dreamTags: string[];
    messageCount: number;
    tokenCount: number;
    characterAffinitySnapshot: number;
    characterAffinityDelta: number;
    fromMessageSequence: number;
    toMessageSequence: number;
    startedAt: string | null;
    finishedAt: string | null;
  }[];
}

interface MessageRow {
  id: string;
  sequence: number;
  role: string;
  formKey: string | null;
  emotion: string | null;
  isFallback: boolean;
  bondDelta: number | null;
  characterAffinityDelta: number | null;
  text: string | null;
  parts: unknown;
  brainRunId: string | null;
  brainRun: {
    status: string;
    safetyVerdict: string;
    totalInputTokens: number;
    totalCachedTokens: number;
    totalOutputTokens: number;
    totalCostCents: number;
    latencyMs: number;
    toolCallRounds: number;
  } | null;
  deletedAt: string | null;
  excludedFromContextAt: string | null;
  purgedAt: string | null;
  createdAt: string | null;
  feedback: { rating: number | null; tags: string[]; comment: string | null } | null;
}

export function ConversationDetailPage() {
  const { id = "" } = useParams();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [includeDeleted, setIncludeDeleted] = useState(false);

  const detail = useQuery({
    queryKey: ["conversation", id],
    queryFn: () => api.get<ConversationDetail | null>(`/api/v1/admin/conversations/${id}`),
  });

  const messages = useQuery({
    queryKey: ["conversation", id, "messages", page, pageSize, includeDeleted],
    queryFn: () =>
      api.get<Paged<MessageRow>>(`/api/v1/admin/conversations/${id}/messages`, {
        page,
        pageSize,
        includeDeleted,
      }),
    placeholderData: keepPreviousData,
  });

  if (detail.error) return <Alert type="error" showIcon message={(detail.error as Error).message} />;
  const c = detail.data;

  return (
    <Space direction="vertical" size={12} style={{ width: "100%" }}>
      <Card title="会话" loading={detail.isLoading} styles={{ body: { padding: 16 } }}>
        <Descriptions size="small" column={3} bordered>
          <Descriptions.Item label="用户">
            <Link to={`/users/${c?.userId}`}>{c?.userName || "(未命名)"}</Link>
          </Descriptions.Item>
          <Descriptions.Item label="关系">
            <Link to={`/users/${c?.relationshipId}`}>{c?.relationshipId?.slice(-8)}</Link>
            {c?.relationshipState && (
              <Typography.Text type="secondary" style={{ marginLeft: 8 }}>
                羁绊 {c.relationshipState.bond} · Lv{c.relationshipState.level} · 好感{" "}
                {c.relationshipState.characterAffinity}
              </Typography.Text>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="入口">
            {c?.product.displayName}（{c?.product.entryKind}）
          </Descriptions.Item>
          <Descriptions.Item label="消息 / 中枢">
            {fmtNumber(c?.messageCount)} / {fmtNumber(c?.brainRunCount)}
          </Descriptions.Item>
          <Descriptions.Item label="状态">
            <StatusTag value={c?.status} />
          </Descriptions.Item>
          <Descriptions.Item label="创建">{fmtDateTime(c?.createdAt)}</Descriptions.Item>
          <Descriptions.Item label="最后消息" span={2}>
            {fmtDateTime(c?.lastMessageAt)}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {c?.rollingSummary && (
        <Card title="滚动摘要（会话超出上下文预算后折叠的历史）" size="small">
          <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
            已覆盖到 sequence #{c.rollingSummaryUpToSequence ?? "—"}。折叠是压缩不是切分——
            会话本身不会因为变长而断开，见 docs/LLM_RUNTIME.md §4。
          </Typography.Paragraph>
          <div style={{ whiteSpace: "pre-wrap" }}>{c.rollingSummary}</div>
        </Card>
      )}

      {(c?.memories.length ?? 0) > 0 && (
        <Card title={`会话摘要 · ${c?.memories.length} 段`} size="small">
          <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
            一个会话可以有多段摘要，每折叠一次产生一段，由 fromMessageSequence ~ toMessageSequence
            界定范围。实测 98% 的会话只有一段。
          </Typography.Paragraph>
          {c?.memories.map((m) => (
            <Descriptions key={m.id} size="small" column={2} bordered style={{ marginBottom: 12 }}>
              <Descriptions.Item label="标题">{m.title || "—"}</Descriptions.Item>
              <Descriptions.Item label="覆盖序号">
                #{m.fromMessageSequence} – #{m.toMessageSequence}
              </Descriptions.Item>
              <Descriptions.Item label="摘要" span={2}>
                {m.summary || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="标签">
                <Tags values={m.tags} />
              </Descriptions.Item>
              <Descriptions.Item label="梦境标签">
                <Tags values={m.dreamTags} />
              </Descriptions.Item>
              <Descriptions.Item label="好感快照 / 增量">
                {m.characterAffinitySnapshot} / {m.characterAffinityDelta > 0 ? "+" : ""}
                {m.characterAffinityDelta}
              </Descriptions.Item>
              <Descriptions.Item label="token">{fmtNumber(m.tokenCount)}</Descriptions.Item>
            </Descriptions>
          ))}
        </Card>
      )}

      <Card
        title="聊天原文"
        extra={
          <Space>
            <Checkbox checked={includeDeleted} onChange={(e) => setIncludeDeleted(e.target.checked)}>
              包含已删除
            </Checkbox>
            <Tag color="orange">查看已记入审计</Tag>
          </Space>
        }
        styles={{ body: { padding: 16 } }}
      >
        <Spin spinning={messages.isFetching}>
          {(messages.data?.items ?? []).map((m) => (
            <div key={m.id} className={`chat-row ${m.role === "user" ? "user" : ""}`}>
              <div style={{ maxWidth: "80%" }}>
                <div className="chat-bubble">
                  {m.purgedAt ? (
                    <Typography.Text type="secondary">（内容已按保留期物理清除）</Typography.Text>
                  ) : (
                    m.text || <Typography.Text type="secondary">（无文本，见 parts）</Typography.Text>
                  )}
                </div>
                <div className="chat-meta" style={{ textAlign: m.role === "user" ? "right" : "left" }}>
                  #{m.sequence} · {m.role}
                  {m.formKey ? ` · ${m.formKey}` : ""}
                  {m.emotion ? ` · ${m.emotion}` : ""}
                  {m.isFallback ? " · 兜底" : ""}
                  {m.bondDelta !== null ? ` · 羁绊+${m.bondDelta}` : ""}
                  {m.characterAffinityDelta !== null ? ` · 好感${m.characterAffinityDelta > 0 ? "+" : ""}${m.characterAffinityDelta}` : ""}
                  {m.deletedAt ? " · 已删除" : ""}
                  {m.excludedFromContextAt ? " · 不入上下文" : ""}
                  {" · "}
                  {fmtDateTime(m.createdAt)}
                  {m.brainRunId && (
                    <>
                      {" · "}
                      <Link to={`/brain/runs/${m.brainRunId}`}>
                        中枢
                        {m.brainRun
                          ? `（${m.brainRun.totalInputTokens}→${m.brainRun.totalOutputTokens} tok, ${m.brainRun.latencyMs}ms）`
                          : ""}
                      </Link>
                    </>
                  )}
                  {m.feedback && (
                    <Tag color={m.feedback.rating === 1 ? "green" : "red"} style={{ marginLeft: 6 }}>
                      {m.feedback.rating === 1 ? "赞" : "踩"}
                      {m.feedback.comment ? `：${m.feedback.comment}` : ""}
                    </Tag>
                  )}
                </div>
              </div>
            </div>
          ))}
        </Spin>

        <Pagination
          style={{ marginTop: 16, textAlign: "right" }}
          current={page}
          pageSize={pageSize}
          total={messages.data?.total ?? 0}
          showSizeChanger
          pageSizeOptions={[20, 50, 100, 200]}
          showTotal={(t) => `共 ${t} 条`}
          onChange={(p, ps) => {
            setPage(p);
            setPageSize(ps);
          }}
        />
      </Card>
    </Space>
  );
}
